import { useState, useEffect } from "react";
import { db } from "lib/firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { formatQTZ } from "../components/utils/format";

/**
 * Converts Firestore Timestamp to Spanish date format
 * @param {Object} timestamp - Firestore Timestamp object or Date
 * @returns {string} Formatted date like "15 Marzo 2024" or empty string if invalid
 */
const formatFirestoreDate = (timestamp) => {
  try {
    // If it's null or undefined, return empty string
    if (!timestamp) return "";

    // If it's a Firestore Timestamp (has seconds property)
    let date;
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      return "";
    }

    // Check if date is valid
    if (isNaN(date.getTime())) return "";

    const months = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${month} ${year}`;
  } catch (error) {
    console.warn("Error formatting date:", error);
    return "";
  }
};

export const useTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // We fetch all active testimonials. 
    // We do client-side sorting to avoid requiring a composite index immediately.
    const q = query(
      collection(db, "testimonios"), // Changed to testimonios
      where("Visible", "==", true) // Changed active to Visible
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const stories = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          const pagoAnterior = data.FacturaAnterior || 0;
          const pagoNuevo = data.FacturaNueva || 0;
          stories.push({
            id: doc.id,
            name: data.Nombre,
            location: String(data.Ubicacion),
            image: "",
            systemSize: data.Paneles || 0,
            monthlyBefore: formatQTZ(pagoAnterior),
            monthlyAfter: formatQTZ(pagoNuevo),
            monthlySavings: formatQTZ(pagoAnterior - pagoNuevo),
            testimonial: data.Testimonio || "Cliente Satisfecho",
            installDate: formatFirestoreDate(data.FechaInstalacion),
            order: data.Orden ? Number(data.Orden) : 999
          });
        });

        // Sort by order 
        stories.sort((a, b) => (a.order || 0) - (b.order || 0));

        setTestimonials(stories);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching testimonials:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { testimonials, loading };
};
